/**
 * Keycloak React Context
 * 
 * Provides authentication state to the React component tree.
 * Wraps the keycloak-js library with React hooks.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  getKeycloak, 
  initKeycloak, 
  login as kcLogin, 
  logout as kcLogout, 
  getUserProfile,
  getToken,
  isAuthenticated as kcIsAuthenticated,
  setSessionCallbacks,
  KeycloakUserProfile 
} from '@/lib/keycloak';
import { useDarkMode } from '@/hooks/useDarkMode';

// Auth state
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  status: AuthStatus;
  user: KeycloakUserProfile | null;
  token: string | undefined;
  isAuthenticated: boolean;
  sessionExpired: boolean;
  login: (redirectUri?: string) => void;
  logout: (redirectUri?: string) => void;
}

/**
 * Session Expired Modal - baked into the provider
 * Shows when refresh token is invalid and user must re-login
 */
function SessionExpiredModal({ onLogin }: { onLogin: () => void }) {
  const [mode] = useDarkMode();
  const isDark = mode === 'dark';

  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div 
        className={`
          rounded-lg shadow-xl p-6 max-w-md mx-4
          ${isDark ? 'bg-zinc-800 text-white border border-zinc-600' : 'bg-white text-zinc-900 border border-zinc-200'}
        `}
      >
        <div className="flex flex-col items-center gap-4 text-center">
          {/* Icon */}
          <div className={`
            rounded-full p-3
            ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}
          `}>
            <svg 
              className={`w-8 h-8 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold">
            Session Expired
          </h2>

          {/* Message */}
          <p className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>
            Your session has expired. Please log in again to continue.
          </p>

          {/* Login Button */}
          <button
            onClick={() => onLogin()}
            className={`
              mt-2 px-6 py-2 rounded-md font-medium transition-colors
              ${isDark 
                ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                : 'bg-blue-500 hover:bg-blue-600 text-white'
              }
            `}
          >
            Log In
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export interface KeycloakProviderProps {
  children: ReactNode;
  /**
   * If true, will redirect to login if not authenticated
   */
  loginRequired?: boolean;
}

/**
 * Keycloak Authentication Provider
 * 
 * Wrap your app with this provider to enable authentication.
 */
export function KeycloakProvider({ children, loginRequired = false }: KeycloakProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<KeycloakUserProfile | null>(null);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Initialize Keycloak on mount
  useEffect(() => {
    let mounted = true;

    // Register session callbacks BEFORE init so they're ready when refresh starts
    setSessionCallbacks(
      // onSessionExpired - refresh token is dead
      () => {
        console.log('[KeycloakProvider] Session expired callback triggered');
        if (mounted) {
          setSessionExpired(true);
          setUser(null);
          setToken(undefined);
        }
      },
      // onTokenRefreshed - got a new access token
      (newToken: string) => {
        if (mounted) {
          setToken(newToken);
        }
      }
    );

    const init = async () => {
      try {
        const authenticated = await initKeycloak(
          // onAuthenticated
          () => {
            if (mounted) {
              setStatus('authenticated');
              setUser(getUserProfile());
              setToken(getToken());
            }
          },
          // onUnauthenticated
          () => {
            if (mounted) {
              setStatus('unauthenticated');
              setUser(null);
              setToken(undefined);
              
              // Redirect to login if required
              if (loginRequired) {
                kcLogin();
              }
            }
          }
        );

        // Also check immediately after init
        if (mounted && authenticated) {
          setStatus('authenticated');
          setUser(getUserProfile());
          setToken(getToken());
        }
      } catch (error) {
        console.error('[KeycloakProvider] Init failed:', error);
        if (mounted) {
          setStatus('unauthenticated');
        }
      }
    };

    init();

    // Setup event listeners for auth state changes
    // Note: onTokenExpired is now handled in keycloak.ts setupTokenRefresh
    const keycloak = getKeycloak();
    
    const onAuthSuccess = () => {
      if (mounted) {
        setStatus('authenticated');
        setUser(getUserProfile());
        setToken(getToken());
        setSessionExpired(false); // Reset on successful auth
      }
    };

    const onAuthLogout = () => {
      // If we're still on the page when this fires, session expired unexpectedly
      // (user-initiated logout redirects away before this would fire)
      if (mounted) {
        console.log('[KeycloakProvider] onAuthLogout - session expired unexpectedly');
        setSessionExpired(true);
        setUser(null);
        setToken(undefined);
      }
    };

    keycloak.onAuthSuccess = onAuthSuccess;
    keycloak.onAuthLogout = onAuthLogout;

    return () => {
      mounted = false;
    };
  }, [loginRequired]);

  const login = useCallback((redirectUri?: string) => {
    kcLogin(redirectUri);
  }, []);

  const logout = useCallback((redirectUri?: string) => {
    kcLogout(redirectUri);
  }, []);

  const value: AuthState = useMemo(() => ({
    status,
    user,
    token,
    isAuthenticated: status === 'authenticated',
    sessionExpired,
    login,
    logout,
  }), [status, user, token, sessionExpired, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {sessionExpired && <SessionExpiredModal onLogin={login} />}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication state
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a KeycloakProvider');
  }
  return context;
}

/**
 * Hook to get just the authentication status (for compatibility)
 */
export function useAuthStatus(): AuthStatus {
  return useAuth().status;
}
